import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  Input,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Divider,
  Form,
  Chip,
  Switch
} from '@heroui/react';
import UsersApi from 'src/api/users';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import { setAuthState } from 'src/store/user/userSlice';
import { errorToast, successToast } from 'src/utils/toasts';
import BillingApi from 'src/api/billing';
import { SubscriptionInfo } from 'src/api/types/billing';
import { UserSettings } from 'src/api/types/user';
import { trackEvent } from 'src/utils/analytics';
import Loader from 'src/components/Loader/Loader';
import NpcLocationSpellingsEditor from 'src/components/NpcLocationSpellingsEditor';
import { SpellingsConfig } from 'src/api/types/spellings';

const Settings = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isSubscriptionLoading, setIsSubscriptionLoading] = useState(false);
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(true);
  const [userSettings, setUserSettings] = useState<UserSettings>({});
  const [isSettingsLoading, setIsSettingsLoading] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [updateInProgress, setUpdateInProgress] = useState(false);
  const [globalSpellings, setGlobalSpellings] = useState<SpellingsConfig>({
    npcs: [],
    locations: []
  });
  const [isLoadingSpellings, setIsLoadingSpellings] = useState(true);

  const user = useAppSelector((state) => state.auth.user);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (user) {
      setFirstName(user.firstName ?? '');
      setLastName(user.lastName ?? '');
    }
  }, [user]);

  useEffect(() => {
    fetchSubscriptionInfo();
  }, []);

  const fetchUserSettings = useCallback(async () => {
    try {
      setIsLoadingSettings(true);
      const response = await UsersApi.fetchUserSettings();
      setUserSettings(response.data);
    } catch (err) {
      console.error('Error fetching user settings:', err);
      errorToast({
        title: 'Error',
        description: 'Failed to load settings. Please refresh the page.'
      });
    } finally {
      setIsLoadingSettings(false);
    }
  }, []);

  const fetchGlobalSpellings = useCallback(async () => {
    try {
      setIsLoadingSpellings(true);
      const response = await UsersApi.fetchGlobalSpellings();
      setGlobalSpellings(response.data);
    } catch (err) {
      console.error('Error fetching global spellings:', err);
      errorToast({
        title: 'Error',
        description: 'Failed to load spellings. Please refresh the page.'
      });
    } finally {
      setIsLoadingSpellings(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchGlobalSpellings();
    }
  }, [user, fetchGlobalSpellings]);

  useEffect(() => {
    if (user?.discordId) {
      fetchUserSettings();
    }
  }, [user?.discordId, fetchUserSettings]);

  const handleUpdateGlobalSpellings = useCallback(async (spellings: SpellingsConfig) => {
    try {
      const response = await UsersApi.updateGlobalSpellings(spellings);
      setGlobalSpellings(response.data);
    } catch (err) {
      console.error('Error updating global spellings:', err);
      errorToast({
        title: 'Error',
        description: 'Failed to update spellings. Please try again.'
      });
      throw err;
    }
  }, []);

  const updateSetting = async <T,>(key: string, newValue: T) => {
    if (updateInProgress) {
      return;
    }

    setUpdateInProgress(true);
    setIsSettingsLoading(true);
    const previousValue = userSettings[key];
    setUserSettings((prev) => ({ ...prev, [key]: newValue }));
    try {
      await UsersApi.updateUserSettings({
        [key]: newValue
      } as Partial<UserSettings>);
      successToast({
        title: 'Preferences updated',
        description: 'Your preferences have been saved.'
      });
    } catch (err) {
      console.error('Error updating user settings:', err);
      setUserSettings((prev) => ({ ...prev, [key]: previousValue }));
      errorToast({
        title: 'Error',
        description: 'Failed to update preferences. Please try again.'
      });
    } finally {
      setIsSettingsLoading(false);
      setUpdateInProgress(false);
    }
  };

  const fetchSubscriptionInfo = async () => {
    try {
      setIsLoadingSubscription(true);
      const response = await BillingApi.getSubscriptionInfo();
      if (response.data.hasSubscription && response.data.subscription) {
        setSubscriptionInfo(response.data.subscription);
      }
    } catch (err) {
      console.error('Error fetching subscription info:', err);
    } finally {
      setIsLoadingSubscription(false);
    }
  };

  async function fetchAndSetUser() {
    const { data } = await UsersApi.fetchCurrentUser();
    const { data: flags } = await UsersApi.fetchFeatureFlags();

    dispatch(
      setAuthState({
        isAuthenticated: true,
        isLoading: false,
        user: data.user,
        featureFlags: flags,
        intercomJwt: data.intercomJwt
      })
    );
  }

  const handleProfileSubmission = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProfileLoading(true);
    setError(null);

    try {
      await UsersApi.updateUserProfile({
        firstName,
        lastName
      });

      await fetchAndSetUser();
      successToast({
        title: 'Profile updated successfully',
        description: 'Your profile has been updated successfully.'
      });
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
      errorToast({
        title: 'Error',
        description: 'Failed to update profile. Please try again.'
      });
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleSubscriptionButtonClick = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubscriptionLoading(true);

    trackEvent('Manage Subscription Clicked', {
      hasSubscription: !!subscriptionInfo,
      subscriptionStatus: subscriptionInfo?.status,
      subscriptionPlan: subscriptionInfo?.productName
    });

    try {
      const res = await BillingApi.createStripeSubscriptionSession();
      const { url } = res.data;

      if (url) {
        // redirect to the Stripe checkout page
        window.location.href = url;
      } else {
        throw new Error('No URL returned from the server');
      }
    } catch (err) {
      console.error('Error creating subscription session:', err);
      errorToast({
        title: 'Error',
        description: 'Failed to create subscription session. Please try again.'
      });
      setIsSubscriptionLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase()
    }).format(amount / 100); // Stripe amounts are in cents
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Active';
      case 'trialing':
        return 'Trial';
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      default:
        return status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ');
    }
  };

  const formatCardBrand = (brand: string) => {
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const formatPromotionalDiscount = (promotionalPeriod: SubscriptionInfo['promotionalPeriod']) => {
    if (!promotionalPeriod) return '';

    if (promotionalPeriod.amountOff) {
      return formatCurrency(promotionalPeriod.amountOff, promotionalPeriod.currency ?? 'usd');
    } else if (promotionalPeriod.percentOff) {
      return `${promotionalPeriod.percentOff}%`;
    }
    return '';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      <Card className="w-full">
        <CardHeader className="text-center flex flex-col items-center">
          <h1 className="text-2xl font-bold">Profile</h1>
          <p className="text-default-500">Update your profile information below.</p>
        </CardHeader>
        <Divider />
        <Form onSubmit={handleProfileSubmission}>
          <CardBody className="flex flex-col gap-4">
            <Input
              label="First Name"
              type="text"
              value={firstName}
              onValueChange={setFirstName}
              isRequired
              isDisabled={isProfileLoading}
            />

            <Input
              label="Last Name"
              type="text"
              value={lastName}
              onValueChange={setLastName}
              isRequired
              isDisabled={isProfileLoading}
            />

            {error && <p className="text-danger text-sm">{error}</p>}
          </CardBody>
          <Divider />
          <CardFooter>
            <Button color="primary" className="w-full" type="submit" isLoading={isProfileLoading}>
              {isProfileLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </CardFooter>
        </Form>
      </Card>

      {user?.discordId && (
        <Card className="w-full">
          <CardHeader className="text-center flex flex-col items-center">
            <h1 className="text-2xl font-bold">Discord Settings</h1>
            <p className="text-default-500">Configure your Discord bot preferences.</p>
          </CardHeader>
          <Divider />
          <CardBody className="flex flex-col gap-4">
            {isLoadingSettings ? (
              <div className="flex justify-center py-4">
                <Loader fullscreen={false} />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">Send "leaving soon" warning</span>
                      <span className="text-xs text-default-500 mt-1">
                        Send a 10 minute warning in the text channel before the bot auto-leaves
                        after you've left the voice channel.
                      </span>
                    </div>
                    <Switch
                      isSelected={userSettings.sendRequesterLeaveWarning ?? true}
                      onValueChange={(value) => updateSetting('sendRequesterLeaveWarning', value)}
                      isDisabled={isSettingsLoading}
                    />
                  </div>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}

      <Card className="w-full">
        <CardHeader className="text-center flex flex-col items-center">
          <h1 className="text-2xl font-bold">Subscription</h1>
          <p className="text-default-500">Manage your subscription and billing.</p>
        </CardHeader>
        <Divider />
        <CardBody className="flex flex-col gap-4">
          {isLoadingSubscription ? (
            <Loader fullscreen={false} />
          ) : subscriptionInfo ? (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Status:</span>
                <span className="text-sm">{getStatusText(subscriptionInfo.status)}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Plan:</span>
                <span className="text-sm">{subscriptionInfo.productName}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Price:</span>
                <span className="text-sm">
                  {formatCurrency(subscriptionInfo.amount, subscriptionInfo.currency)}/
                  {subscriptionInfo.interval}
                </span>
              </div>

              {subscriptionInfo.promotionalPeriod && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Discount:</span>
                  <span className="text-sm text-success">
                    {formatPromotionalDiscount(subscriptionInfo.promotionalPeriod)} off
                  </span>
                </div>
              )}

              {subscriptionInfo.currentPeriodStart && subscriptionInfo.currentPeriodEnd && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Billing Period:</span>
                  <span className="text-sm">
                    {formatDate(subscriptionInfo.currentPeriodStart)} -{' '}
                    {formatDate(subscriptionInfo.currentPeriodEnd)}
                  </span>
                </div>
              )}

              {subscriptionInfo.paymentMethod && (
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Payment Method:</span>
                  <span className="text-sm">
                    {subscriptionInfo.paymentMethod.card ? (
                      <>
                        {formatCardBrand(subscriptionInfo.paymentMethod.card.brand)} ••••{' '}
                        {subscriptionInfo.paymentMethod.card.last4}
                      </>
                    ) : subscriptionInfo.paymentMethod.type === 'link' ? (
                      'Stripe Link'
                    ) : (
                      subscriptionInfo.paymentMethod.type.charAt(0).toUpperCase() +
                      subscriptionInfo.paymentMethod.type.slice(1)
                    )}
                  </span>
                </div>
              )}

              {subscriptionInfo.cancelAtPeriodEnd && (
                <Chip color="warning" variant="flat" className="w-full">
                  Subscription will cancel at the end of the current period
                </Chip>
              )}

              {subscriptionInfo.promotionalPeriod && (
                <Chip color="success" variant="flat" className="w-full">
                  Promotional period active: {subscriptionInfo.promotionalPeriod.promotionCode}
                  {subscriptionInfo.promotionalPeriod.end && (
                    <span className="ml-1">
                      (ends {formatDate(subscriptionInfo.promotionalPeriod.end)})
                    </span>
                  )}
                </Chip>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-sm text-default-500 mb-4">No active subscription found.</p>
            </div>
          )}
        </CardBody>

        <Divider />

        <CardFooter>
          <Form onSubmit={handleSubscriptionButtonClick} className="w-full">
            <Button
              color="secondary"
              className="w-full"
              type="submit"
              isLoading={isSubscriptionLoading}>
              {subscriptionInfo ? 'Manage Subscription' : 'Subscribe Now'}
            </Button>
          </Form>
        </CardFooter>
      </Card>

      <Card className="w-full">
        {isLoadingSpellings ? (
          <div className="flex justify-center py-4">
            <Loader fullscreen={false} />
          </div>
        ) : (
          <NpcLocationSpellingsEditor
            spellings={globalSpellings}
            onUpdate={handleUpdateGlobalSpellings}
            title="Global NPC & Location Spellings"
            description="We leverage these spellings to try and improve the accuracy of transcribing your sessions."
          />
        )}
      </Card>
    </div>
  );
};

export default Settings;
